/**
 * Filtres des tables des deux étapes.
 *
 * Les valeurs vides ne partent pas à l'API : la v1 appendait des paramètres vides
 * (`contract_id__in=`) parce qu'elle testait `!== ''` sur des tableaux.
 */

/**
 * Une valeur de filtre : `null` pour un choix unique vide, `[]` pour un multiple.
 *
 * Les choix uniques restent des chaînes — c'est ce que rend un `Select` MUI, et c'est
 * aussi la forme attendue en paramètre d'URL : rien à convertir de part et d'autre.
 */
export type FilterValue = string | null | (string | number)[];

export type TableFilters = Record<string, FilterValue>;

/** Aucun filtre posé : sert d'état initial et de réinitialisation. */
export const NO_FILTERS: TableFilters = {};

/** Garde de type : ce qui n'est pas vide est une chaîne non vide ou un tableau peuplé. */
function isEmpty(value: FilterValue): value is null | '' | [] {
  // La chaîne vide est la valeur de l'option « Tous » : elle ne restreint rien, donc
  // elle ne doit pas partir en paramètre — c'est le `contract_id__in=` de la v1.
  return value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

/**
 * Vrai dès qu'un filtre est posé, pour signaler que la liste est restreinte.
 *
 * `names` restreint l'examen à ces seuls filtres. Le bouton « Réinitialiser » s'en sert
 * pour ne s'activer que sur ce qu'il videra réellement : l'état et la recherche vivent
 * hors du bloc dépliable et lui échappent.
 */
export function hasActiveFilters(filters: TableFilters, names?: string[]): boolean {
  const entries = Object.entries(filters).filter(
    ([name]) => names === undefined || names.includes(name)
  );

  return entries.some(([, value]) => !isEmpty(value));
}

/**
 * Retire les filtres nommés et laisse les autres intacts.
 *
 * Le bloc dépliable ne remet à zéro que ce qu'il affiche : l'état et la recherche, posés
 * ailleurs dans la barre d'outils, survivent à sa réinitialisation.
 */
export function clearFilters(filters: TableFilters, names: string[]): TableFilters {
  return Object.fromEntries(Object.entries(filters).filter(([name]) => !names.includes(name)));
}

/**
 * Filtres et recherche réunis pour l'appel : `search` est un paramètre comme un autre.
 *
 * Le texte est nettoyé de ses espaces de bord ; vidé, il retombe sur la chaîne vide, que
 * `toQueryParams` écarte déjà.
 */
export function withSearch(filters: TableFilters, search: string): TableFilters {
  return { ...filters, search: search.trim() };
}

/**
 * Paramètres à passer aux hooks de liste. Les tableaux partent en CSV, forme attendue
 * par les filtres `__in` de l'API.
 */
export function toQueryParams(filters: TableFilters): Record<string, string> {
  return Object.entries(filters).reduce<Record<string, string>>((params, [name, value]) => {
    if (isEmpty(value)) {
      return params;
    }

    if (Array.isArray(value)) {
      return { ...params, [name]: value.join(',') };
    }

    return { ...params, [name]: value };
  }, {});
}
